/**
 * What a city page may claim about a market.
 *
 * The backend already refuses to describe a market it cannot see — `markets.MinSample
 * = 20`, and `/markets/{city}` 404s below that. The web read that 404 as PERMISSION to
 * compute the median itself from current inventory, so a city with a single listing
 * published a median, a $/sqft and a "Typical GoReal savings" figure derived from it.
 *
 * 221 of 268 live city pages hold fewer than 20 listings; 92 hold exactly one.
 */

/**
 * The smallest inventory that can describe a market.
 *
 * Matches the backend's `markets.MinSample` and the existing MIN_SPOTLIGHT_SAMPLE,
 * so all three agree. A fourth number here would be a fourth opinion about the same
 * question.
 */
export const MIN_CITY_SAMPLE = 20;

/**
 * The counties the MLSListings feed actually carries.
 *
 * Not a guess: it is the whole reason `/homes/san-francisco` shows 44 homes
 * while `/homes/san-jose` shows 851, and why Aptos (pop. ~6,600) carries more
 * active listings than San Francisco and Oakland combined.
 */
export const FEED_COUNTIES = [
  'Santa Clara',
  'San Mateo',
  'Santa Cruz',
  'Monterey',
  'San Benito',
] as const;

/**
 * City slug → county, for the three Bay Area counties **outside** that
 * footprint. Only cities we can name are listed: an unknown slug returns `null`
 * rather than an assumption, because a wrong county in a coverage notice is a
 * worse lie than no notice at all.
 *
 * Marin, Sonoma, Napa and Solano are outside the footprint too and are
 * deliberately absent — they were not part of the audited evidence.
 *
 * This table records **which county a city is in**, and nothing else. It used
 * to be described as holding "counties whose emptiness has been measured",
 * which the measurement below refutes: these counties are not empty.
 */
const CITY_COUNTY_OUTSIDE_FEED: Readonly<Record<string, string>> = {
  // San Francisco County — one city.
  'san-francisco': 'San Francisco',

  // Alameda County.
  alameda: 'Alameda',
  albany: 'Alameda',
  ashland: 'Alameda',
  berkeley: 'Alameda',
  'castro-valley': 'Alameda',
  cherryland: 'Alameda',
  dublin: 'Alameda',
  emeryville: 'Alameda',
  fremont: 'Alameda',
  hayward: 'Alameda',
  livermore: 'Alameda',
  newark: 'Alameda',
  oakland: 'Alameda',
  piedmont: 'Alameda',
  pleasanton: 'Alameda',
  'san-leandro': 'Alameda',
  'san-lorenzo': 'Alameda',
  sunol: 'Alameda',
  'union-city': 'Alameda',

  // Contra Costa County.
  alamo: 'Contra Costa',
  antioch: 'Contra Costa',
  'bay-point': 'Contra Costa',
  'bethel-island': 'Contra Costa',
  blackhawk: 'Contra Costa',
  brentwood: 'Contra Costa',
  byron: 'Contra Costa',
  clayton: 'Contra Costa',
  concord: 'Contra Costa',
  crockett: 'Contra Costa',
  danville: 'Contra Costa',
  diablo: 'Contra Costa',
  'discovery-bay': 'Contra Costa',
  'el-cerrito': 'Contra Costa',
  'el-sobrante': 'Contra Costa',
  hercules: 'Contra Costa',
  kensington: 'Contra Costa',
  knightsen: 'Contra Costa',
  lafayette: 'Contra Costa',
  martinez: 'Contra Costa',
  moraga: 'Contra Costa',
  'north-richmond': 'Contra Costa',
  oakley: 'Contra Costa',
  orinda: 'Contra Costa',
  pacheco: 'Contra Costa',
  pinole: 'Contra Costa',
  pittsburg: 'Contra Costa',
  'pleasant-hill': 'Contra Costa',
  'port-costa': 'Contra Costa',
  richmond: 'Contra Costa',
  rodeo: 'Contra Costa',
  'san-pablo': 'Contra Costa',
  'san-ramon': 'Contra Costa',
  'walnut-creek': 'Contra Costa',
};

/**
 * The county a city sits in, when that county is outside the feed's footprint.
 * `null` means either "covered" or "we don't know this name" — both of which
 * must produce silence, never a claim.
 */
export function outsideFeedCounty(slug: string): string | null {
  if (!slug) return null;
  return CITY_COUNTY_OUTSIDE_FEED[slug.toLowerCase()] ?? null;
}

/** The counties the feed covers, as an English list. */
function feedCountyList(): string {
  const all = [...FEED_COUNTIES];
  const last = all.pop();
  return `${all.join(', ')} and ${last}`;
}

/**
 * The quiet, factual version of "why are there only 44 homes here".
 *
 * **What was measured, and when.** 2026-08-02, `POST /api/v1/search` with
 * `{cities:[name], per_page:1}`, one call per slug in the table above plus 36
 * cities inside the footprint as a control. Controls first: `san-jose` returned
 * 851 and the invented `atlantis` returned 0, so a zero in this run is a real
 * zero and not a broken query.
 *
 *   outside the footprint   Fremont 78 · Hayward 46 · San Francisco 44 ·
 *                           Oakland 36 · Newark 25 · San Ramon 22 · Dublin 18 ·
 *                           Pleasanton 17 · Union City 16 · Antioch 13 ·
 *                           Livermore 12 · Castro Valley 10 · Concord 9 …
 *                           Alameda County 279 across 16 of its 19 slugs,
 *                           Contra Costa 113 across 23 of its 34.
 *   inside the footprint    Santa Cruz 110 · Salinas 104 · Carmel 102 ·
 *                           Hollister 100 · Morgan Hill 100 · Aptos 89 ·
 *                           San Mateo 76 · Half Moon Bay 22 · Woodside 20 ·
 *                           Atherton 9 · Brisbane 3 · Colma 0.
 *
 * That refutes what this sentence used to imply. It said "we often carry
 * nothing here, however active the local market is" — over a county holding
 * 279 homes. And it told Fremont's buyer (78 homes) that their county was
 * "outside that footprint" while San Mateo's buyer (76 homes) was told nothing
 * at all, because San Mateo is one of the counties the feed is built on. The
 * asymmetry is real and worth keeping, but it is not the one the copy implied:
 * emptiness was never the fact. **Partialness** is.
 *
 * So the note claims only what the counts support: these homes reached us
 * through a single MLS, and where that MLS is not the county's own, its view is
 * partial. Never "we do not cover this city" — we plainly do carry homes in 40
 * of the 53 — and never anything about whether the local market is busy or
 * quiet, which nothing here can measure.
 */
function footprintNote(county: string, total: number): string {
  const carried =
    (total ?? 0) > 0
      ? `the ${total.toLocaleString()} ${total === 1 ? 'home' : 'homes'} here are what reaches that one feed — a partial view of this market, not all of it`
      : `our view here is partial, and at this moment it holds nothing under this name. Homes may well be for sale that this feed does not carry`;
  return (
    `Our listings come from a single MLS, MLSListings, which is built on ${feedCountyList()} counties. ` +
    `${county} County is not one of them, so ${carried}.`
  );
}

export interface CityCoverage {
  /** Whether a median, $/sqft or savings figure may be printed for this city. */
  canPublishMarketFigures: boolean;
  /** Whether we hold any listings at all. */
  hasInventory: boolean;
  /** Whether we hold some, but too few to describe the market. */
  isThin: boolean;
  /** A plain sentence for the page, or null when there is nothing to explain. */
  note: string | null;
  /**
   * Whether the market-stats call FAILED, as opposed to answering "too thin".
   *
   * The page renders its own line for this: everything else here is a statement
   * about our coverage, and "we could not reach the stats service" is a
   * statement about us. Keeping them apart is what stops one being read as the
   * other.
   */
  statsUnavailable: boolean;
}

export function cityCoverage(input: {
  total: number;
  hasStats: boolean;
  /** City slug, when the caller has one. Enables the footprint notice. */
  citySlug?: string;
  /**
   * Did the inventory fetch succeed?
   *
   * `false` means `total` is a placeholder zero, not a count — the city page
   * already keeps that state distinct ("We couldn't load Fremont listings just
   * now"), and a coverage sentence printed underneath it turned a failed fetch
   * into a definite statement about what we hold. A failure to measure must not
   * share a voice with a measurement. Defaults to `true` so callers that always
   * have a count need not say so.
   */
  inventoryKnown?: boolean;
  /**
   * Did the market-stats call succeed?
   *
   * `hasStats: false` has always carried two meanings and only ever spoke one of
   * them. `GET /markets/{city}` answers **404** when the city is under
   * `MinSample`, and `getMarketStats` used to `.catch(() => null)`, so a 500, a
   * timeout or a dropped connection arrived here identically — and the thin-city
   * sentence below then told a San Jose buyer, one line under "851 listings",
   * that we hold fewer than 20 homes there. A count that contradicts the count
   * beside it, minted from a failed request.
   *
   * `false` therefore means "we could not ask", and the only honest consequence
   * is silence plus `statsUnavailable` for the page to speak to. Defaults to
   * `true` so callers that always have an answer need not say so.
   */
  statsKnown?: boolean;
}): CityCoverage {
  const { total, hasStats, citySlug, inventoryKnown = true, statsKnown = true } = input;

  // The backend is the authority: it supplies stats only when it has the sample to
  // justify them. Second-guessing it with our own count is how two numbers for one
  // fact drift apart.
  const canPublishMarketFigures = hasStats;
  const hasInventory = total > 0;
  // Thin is a MEASUREMENT — "the backend looked and declined" — so it cannot be
  // inferred from a call that never came back. Without this the note below is a
  // failed request wearing the clothes of a fact about the city.
  const statsUnavailable = !statsKnown;
  const isThin = hasInventory && !canPublishMarketFigures && statsKnown;

  // Precedence: the footprint is the deeper fact and explains thinness as well,
  // so where both apply it is the one sentence worth spending. A city with 44
  // homes is NOT thin by the backend's reckoning and would otherwise say
  // nothing — which is how a San Francisco buyer read "44" as "the market is
  // empty" rather than "we don't carry this MLS".
  const gapCounty = citySlug && inventoryKnown ? outsideFeedCounty(citySlug) : null;

  let note: string | null = null;
  if (!inventoryKnown) {
    // Nothing is known about this city right now. Both notes below quote a
    // count, and the count is a fiction in this state.
    note = null;
  } else if (gapCounty) {
    note = footprintNote(gapCounty, total);
  } else if (isThin) {
    // About OUR FEED, not about the city. "Berkeley has 6 homes" is false about the
    // world; "our feed has 6 in Berkeley" is true and is the thing a buyer needs in
    // order to judge what they are looking at.
    //
    // **It used to name where our coverage is thinnest, and it named it wrong.**
    // The sentence read "the MLSListings feed, whose coverage is thinnest in San
    // Francisco and the East Bay". It cannot: `gapCounty` wins the precedence
    // above for every slug in the outside-the-footprint table, so San Francisco
    // and the whole East Bay take the footprint note and never reach this line
    // at all. The sentence now claims only what the count in front of the buyer
    // supports — one feed's view, too small a sample for a median — and nothing
    // about which region we cover worst, which this function has no way to know.
    //
    // **The correction that replaced it was also wrong, in the other
    // direction.** It asserted that "the only cities that ever read this
    // sentence" were eleven named ones, "every one of them in San Mateo
    // County". Nothing narrows the branch that way. What actually reaches it is
    // every slug that has inventory, has no market stats, and is absent from
    // `CITY_COUNTY_OUTSIDE_FEED` — and that table names 54 slugs out of a feed
    // carrying hundreds.
    //
    // Measured 2026-08-03 against staging
    // (`https://goreal-api-445316469650.us-west1.run.app`):
    //
    //   GET /api/v1/cities                  → 267 city rows, 4,140 active listings
    //   GET /api/v1/markets/{slug} ×267     → 200 for 48 slugs, 404 for the rest
    //
    // Controls, so a 404 in that sweep is a real "no stats" and not a broken
    // request: `/markets/san-jose` answered 200 (853 active) and the invented
    // `/markets/atlantis` answered 404 — and `atlantis` carries no row in
    // `/api/v1/cities` either. Every one of the 40 cities `/api/v1/markets`
    // publishes also answered 200 individually, so the two surfaces agree.
    //
    // That leaves **185 of the 267 city rows reading this sentence**, holding
    // 659 listings between them: 86 hold exactly one home, 57 hold two to four,
    // 23 five to nine, 19 ten to nineteen.
    //
    // They are not a San Mateo phenomenon. All five feed counties are in the
    // set — Monte Sereno 3 (Santa Clara), Brisbane 3 (San Mateo), Davenport 2
    // (Santa Cruz), Gonzales 1 (Monterey), San Juan Bautista 9 (San Benito) —
    // and all five were confirmed rendering the note on the deployed build the
    // same day (`GET /homes/{slug}`, grep for the closing clause; `/homes/san-jose`
    // as the control renders neither note). It also reaches names the
    // outside-the-footprint table has never heard of, which is what makes the
    // old claim so far off: San Rafael 2, Santa Rosa 2, Napa 6, Vallejo 13 —
    // and past the Bay Area entirely, Modesto 14, Merced 10, Fresno 7, South
    // Lake Tahoe 3, San Diego 2.
    //
    // **The per-county totals that used to sit here are gone deliberately.**
    // The old text said San Mateo "holds 527 across 25"; an earlier brief for
    // the same work said 497. Two numbers for one quantity means at least one
    // was wrong — and neither is checkable, because **no public endpoint
    // carries a county**. `county_name` exists on the `listings` table and in
    // the admin payload, but `/api/v1/cities` is `{name, count}` and the public
    // listing JSON has no `countyName` field (both confirmed 2026-08-03). No
    // command available here can produce a San Mateo total, so no San Mateo
    // total is stated.
    //
    // The Alameda and Contra Costa figures in `footprintNote` are a different
    // kind of number and stay: they are arithmetic over the slugs THIS FILE
    // enumerates, not a claim about a county line, which is why re-running the
    // same sum a day later reproduced them to within one listing each. They are
    // quoted once, up there, and are not restated here — a figure that appears
    // twice is a figure that can disagree with itself.
    note =
      `We list ${total} ${total === 1 ? 'listing' : 'listings'} in this city right now. ` +
      `They all reach us through a single MLS, MLSListings, so this is that one feed's ` +
      `view of the market rather than all of it — and we don't publish a median until ` +
      `we hold at least ${MIN_CITY_SAMPLE} homes here.`;
  }

  return { canPublishMarketFigures, hasInventory, isThin, note, statsUnavailable };
}

/**
 * The same fact, for `/homes?cities=…`.
 *
 * **Pass the cities the homes on screen are actually in, not the filter.** The
 * search response's own `city` facet is that list — `?cities=san-francisco,
 * atlantis` answers `{"San Francisco": 44}` — and it is the only input that
 * makes this function's rule both safe and useful. See `search-client.tsx`.
 *
 * The rule: **every** city named must be one this module can place in a county
 * outside the footprint. `outsideFeedCounty` returns `null` for a covered city
 * *and* for a name it has never heard of — `san-jose` and `atlantis` are
 * indistinguishable here — so a single unnamed city silences the notice. That
 * is deliberate and the direction to fail in: firing over San Jose's 851 real
 * homes would be a false claim about the cards on screen, which is the very
 * defect this whole module exists to remove; staying quiet is only a missed
 * explanation.
 *
 * This used to be documented as "fires when every RECOGNISED city is outside",
 * which was never what the code did. Feeding it the result facet is what makes
 * the honest rule fire in the case that comment was reaching for: a filter can
 * name a city that contributes nothing, and a city contributing nothing cannot
 * make the notice false.
 */
export function feedGapForCities(slugs: string[]): string | null {
  if (slugs.length === 0) return null;

  const counties: string[] = [];
  for (const slug of slugs) {
    const county = outsideFeedCounty(slug);
    if (!county) return null;
    if (!counties.includes(county)) counties.push(county);
  }
  // No `counties.length === 0` guard: the loop above returns early on the only
  // input that could leave it empty, so such a check was dead code claiming to
  // be a safety net.

  const last = counties.pop() as string;
  const multiple = counties.length > 0;
  const named = multiple ? `${counties.join(', ')} and ${last}` : last;
  const subject = multiple ? 'counties are' : 'County is';
  const area = multiple ? 'those areas' : 'that area';
  return (
    `Our listings come from a single MLS, MLSListings, which is built on ${feedCountyList()} counties. ` +
    `${named} ${subject} not among them, so what you see here is that one feed's partial view ` +
    `of ${area} rather than every home for sale there.`
  );
}
